import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import {
  Box,
  Button,
  Input,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  Text,
  Link,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorMode,
  Flex,
  Image,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { Helmet } from "react-helmet";
import useCheckAuth from '../hooks/useCheckAuth';

// Validation schema using Yup
const schema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required')
    .trim(),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const toast = useToast();
  useCheckAuth(true); // Keep this to handle authenticated users trying to access login page

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setLoginError('');
    
    try {
      console.log('1. Starting login process');
      console.log('2. Current location:', window.location.pathname);
      
      const response = await axios.post('/api/login', data);
      console.log('3. Login response received:', {
        success: response.data.success,
        hasToken: !!response.data.data?.token,
        hasRefreshToken: !!response.data.data?.refreshToken
      });
      
      if (response.data.success) {
        console.log('4. Login successful, storing tokens');
        // Store tokens in localStorage
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        
        console.log('5. Tokens stored in localStorage:', {
          token: localStorage.getItem('token') ? 'present' : 'missing',
          refreshToken: localStorage.getItem('refreshToken') ? 'present' : 'missing'
        });
        
        console.log('6. Showing success toast');
        toast({
          title: 'Login Successful',
          description: 'Welcome back!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'solid'
        });
        
        console.log('7. Attempting navigation to dashboard');
        console.log('8. Current location before navigation:', window.location.pathname);
        
        // Let useCheckAuth handle the navigation
        window.location.href = '/dashboard';
      } else {
        console.log('Login failed:', response.data.message);
        setLoginError(response.data.message || 'Login failed');
        toast({
          title: 'Login Failed',
          description: response.data.message || 'Login failed',
          status: 'error',
          duration: 5000,
          isClosable: true,
          variant: 'solid'
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred. Please try again.';
      setLoginError(errorMessage);
      toast({
        title: 'Login Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        variant: 'solid'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | AI Call System</title>
        <meta name="description" content="Login to your AI Call System account" />
      </Helmet>
      
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}
        p={4}
      >
        <Box
          w="full"
          maxW="md"
          p={8}
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          bg={colorMode === 'light' ? 'white' : 'gray.800'}
        >
          <Flex direction="column" align="center" mb={8}>
            <Image
              src={colorMode === 'light' ? "/logo.png" : "/logo-dark.png"}
              alt="Logo"
              width="200px"
              mb={4}
            />
            <Heading size="lg" textAlign="center" mb={2}>
              Welcome Back
            </Heading>
            <Text color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
              Sign in to continue to your account
            </Text>
          </Flex>

          {loginError && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {loginError}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4}>
              <FormControl isInvalid={errors.email}>
                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>Email</FormLabel>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  {...register('email')}
                  autoFocus
                  autoComplete="email"
                  bg={colorMode === 'light' ? 'white' : 'whiteAlpha.100'}
                  borderColor={colorMode === 'light' ? 'gray.200' : 'whiteAlpha.300'}
                  _hover={{
                    borderColor: colorMode === 'light' ? 'gray.300' : 'whiteAlpha.400'
                  }}
                  _focus={{
                    borderColor: 'brand.500',
                    boxShadow: `0 0 0 1px var(--chakra-colors-brand-500)`
                  }}
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.password}>
                <FormLabel color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    {...register('password')}
                    autoComplete="current-password"
                    bg={colorMode === 'light' ? 'white' : 'whiteAlpha.100'}
                    borderColor={colorMode === 'light' ? 'gray.200' : 'whiteAlpha.300'}
                    _hover={{
                      borderColor: colorMode === 'light' ? 'gray.300' : 'whiteAlpha.400'
                    }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: `0 0 0 1px var(--chakra-colors-brand-500)`
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      color={colorMode === 'light' ? 'gray.600' : 'gray.400'}
                      _hover={{
                        bg: colorMode === 'light' ? 'gray.100' : 'whiteAlpha.200'
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                colorScheme="brand"
                width="full"
                size="lg"
                isLoading={isLoading}
                loadingText="Signing in..."
                spinner={<Spinner size="sm" />}
                disabled={isSubmitting}
                _hover={{
                  bg: 'brand.600'
                }}
              >
                Sign In
              </Button>

              <Flex justify="space-between" width="full" mt={2}>
                <Link
                  as={RouterLink}
                  to="/forgot-password"
                  color="brand.500"
                  fontSize="sm"
                  _hover={{ 
                    color: 'brand.600',
                    textDecoration: 'none'
                  }}
                >
                  Forgot password?
                </Link>
                {/* <Link
                  as={RouterLink}
                  to="/register"
                  color="brand.500"
                  fontSize="sm"
                  _hover={{ 
                    color: 'brand.600',
                    textDecoration: 'none'
                  }}
                >
                  Create account
                </Link> */}
              </Flex>
            </VStack>
          </form>
        </Box>
      </Flex>
    </>
  );
};

export default LoginPage;
